import type { AssignmentListItem } from '@repo/types/assignments';
import type { ComponentProps } from 'react';

import { cn } from '@repo/lib/cn';
import { format, isAfter, isBefore } from 'date-fns';
import { ArrowUpRightIcon, BookmarkCheckIcon, PencilLineIcon, RepeatIcon, TimerIcon, TimerOffIcon } from 'lucide-react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useCredentials } from '@/store/auth-store';

interface AssignmentCardProps extends ComponentProps<'div'> {
  data: AssignmentListItem;
}

enum AvailabilityState {
  Before,
  Available,
  After,
}

export default function AssignmentCard(assignmentCardProps: AssignmentCardProps) {
  const { data, className, ...props } = assignmentCardProps;

  const availability: AvailabilityState = useMemo<AvailabilityState>(() => {
    const now = new Date();

    if (data.dueTo && isAfter(now, data.dueTo)) {
      return AvailabilityState.After;
    } else if (data.available && isAfter(now, data.available)) {
      return AvailabilityState.Available;
    } else if (data.available && isBefore(now, data.available)) {
      return AvailabilityState.Before;
    }
    return AvailabilityState.Available;
  }, [data]);

  const secondaryText = () => {
    switch (availability) {
      case AvailabilityState.Before:
        return (
          data.available && (
            <>
              <TimerIcon className="size-4" />
              {`Dostępne od: ${format(data.available, 'dd MMM yyyy')}`}
            </>
          )
        );
      case AvailabilityState.Available:
        return data.dueTo ? (
          <>
            <TimerIcon className="size-4" />
            {`Wykonaj do: ${format(data.dueTo, 'dd MMM yyyy')}`}
          </>
        ) : (
          <>
            <TimerOffIcon className="size-4" />
            {'Brak terminu'}
          </>
        );
      case AvailabilityState.After:
        return (
          data.dueTo && (
            <>
              <TimerOffIcon className="size-4" />
              {`Wygasł o: ${format(data.dueTo, 'dd MMM yyyy')}`}
            </>
          )
        );
    }
  };

  return (
    <div
      className={cn(
        'transition-outline border-border-primary bg-background outline-focus-ring flex h-fit flex-col rounded-2xl border outline-0 focus-visible:outline-3 disabled:opacity-75',
        className
      )}
      {...props}
    >
      <div className={'border-border-primary flex items-center gap-3 border-b p-4'}>
        <h5 className={'truncate text-lg font-semibold'}>{data.name}</h5>
        <Badge className="ms-auto shrink-0" variant={data.type === 'quiz' ? 'brand' : 'muted'} border>
          {data.type === 'quiz' ? (
            <>
              <BookmarkCheckIcon />
              Quiz
            </>
          ) : (
            <>
              <PencilLineIcon />
              Zadanie
            </>
          )}
        </Badge>
      </div>
      <div className={'flex h-full flex-col gap-3 p-4'}>
        <div className="text-muted-foreground flex items-center gap-2 text-sm">{secondaryText()}</div>
        <div className="flex items-end justify-between gap-2">
          <AssignmentCardAttempts data={data} />
          <AssignmentCardActionButton data={data} />
        </div>
      </div>
    </div>
  );
}

interface AssignmentCardAttemptsProps {
  data: AssignmentListItem;
}

function AssignmentCardAttempts(assignmentCardAttemptsProps: AssignmentCardAttemptsProps) {
  const { data } = assignmentCardAttemptsProps;
  const { lvl } = useCredentials();

  if (lvl) return null;

  return (
    <Badge variant={data.attemptCount > 0 ? 'blue' : 'muted'}>
      <RepeatIcon />
      Podejścia: {data.attemptCount}
      {data.maxAttempts > 0 && ` / ${data.maxAttempts}`}
    </Badge>
  );
}

interface AssignmentCardActionButtonProps {
  data: AssignmentListItem;
}

function AssignmentCardActionButton(assignmentCardActionButtonProps: AssignmentCardActionButtonProps) {
  const { data } = assignmentCardActionButtonProps;
  const navigate = useNavigate();
  const { lvl } = useCredentials();

  if (lvl) {
    return (
      <Button variant={'outline'} onClick={() => navigate(`/manage/assignments/${data.id}`)}>
        Podgląd
      </Button>
    );
  }

  return (
    <Button variant={'outline'} onClick={() => navigate(`/assignments/${data.id}`)}>
      Otwórz
      <ArrowUpRightIcon />
    </Button>
  );
}
